import { creatorbase, creatorbaseQueries } from '@round/api';
import { Modal } from '@round/whitelabel-ui';
import { Button } from '@round/whitelabel-ui';
import { useMutation } from '@tanstack/react-query';
import { asMoney, showNotification } from 'helpers';
import { PurchaseOrderAllocation } from '../../usePurchaseOrderAllocations';
import { CampaignPOAllocation } from '../../useCampaignAllocations';
import { useCampaign, usePurchaseOrder } from '@round/creatorbase-helpers/api-hooks';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { Link } from 'react-router-dom';
import { getCampaignLink } from 'Modules/Plans/Campaigns/helpers';
import { Banner } from '@round/whitelabel-ui';
import styles from './DeleteAllocation.module.css';

const deleteOptions = creatorbaseQueries.purchaseOrderLines.options.delete;
const deleteUtils = creatorbaseQueries.purchaseOrderLines.utils.delete;

type Allocation = PurchaseOrderAllocation | CampaignPOAllocation;

type Props<T extends Allocation> = {
    isOpen: boolean;
    onClose: () => void;
    allocation: T | null;
    onDeleted: (allocation: creatorbase.PurchaseOrderLine) => void;
};

const DeleteAllocation = <T extends Allocation>({ isOpen, onClose, allocation, onDeleted }: Props<T>) => {
    const { getCurrencyById } = useCurrencies();
    const { data: campaign } = useCampaign(allocation?.campaign_id, {
        enabled: !!allocation && !('campaign' in allocation),
        initialData:
            !!allocation && 'campaign' in allocation
                ? (allocation as PurchaseOrderAllocation).campaign ?? undefined
                : undefined,
    });

    const { data: purchaseOrder } = usePurchaseOrder(allocation?.purchase_order_id, {
        enabled: !!allocation && !('purchaseOrder' in allocation),
        initialData:
            !!allocation && 'purchaseOrder' in allocation
                ? (allocation as CampaignPOAllocation).purchaseOrder ?? undefined
                : undefined,
    });

    const currency = getCurrencyById(purchaseOrder?.currency_id || campaign?.currency_id);

    const { mutate, isPending } = useMutation(deleteOptions(allocation?.id));
    const handleDelete = () => {
        if (!allocation) {
            return;
        }

        mutate(undefined, {
            onSuccess: () => {
                onDeleted(allocation);
                showNotification('Allocation deleted', 'info');
                onClose();
            },
            onError: (error) => {
                if (deleteUtils.isExpectedErrorResponse(error)) {
                    const response = error.response;
                    showNotification(response.data.detail, 'error');
                    return;
                }

                showNotification('Could not delete allocation', 'error');
            },
        });
    };

    return (
        <Modal className={styles.modal} closeOnEscape closeOnOverlayClick isOpen={isOpen} onClose={onClose}>
            <Modal.Header>
                <Modal.Title>Delete Campaign Allocation</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.copy}>
                <p className={styles.bold}>
                    You're about to permanently delete the following from&nbsp;
                    {purchaseOrder?.number || purchaseOrder?.request_reference}
                </p>
                <Banner appearance="destructive">
                    <p>
                        <span className={styles.bold}>Amount: </span>
                        {asMoney(allocation?.amount, currency)}
                    </p>
                    <p>
                        <span className={styles.bold}>Campaign: </span>
                        <Link className={styles.link} to={getCampaignLink(campaign)} target="_blank">
                            {campaign?.name}
                        </Link>
                    </p>
                </Banner>
            </Modal.Body>

            <Modal.Actions className={styles.actions}>
                <Button appearance="ghost" type="button" onClick={onClose}>
                    Cancel
                </Button>
                <Button appearance="critical" isLoading={isPending} onClick={handleDelete}>
                    Delete
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default DeleteAllocation;
